import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/Card';
import { ArrowLeft, Utensils, TrendingUp, Gauge, Tag, FileText, Pencil } from 'lucide-react';
import { LocationDisplay } from '@/shared/components/ui/LocationDisplay';
import { feedApi } from '@/api/feedApi';
import type { FeedRecord } from '@/api/feedApi';
import { buildRoute } from '@/shared/lib/constants';
import { MetricBox, StatusBadge } from '@/features/shared/components/records';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { format } from 'date-fns';

const appetiteStars = (appetite: number): string => {
  const stars = Math.round(appetite);
  return '\u2B50'.repeat(Math.min(stars, 5));
};

const appetiteColor = (appetite: number): 'emerald' | 'amber' | 'red' => {
  if (appetite >= 4) return 'emerald';
  if (appetite >= 2) return 'amber';
  return 'red';
};

const fcrColor = (fcr: number | null | undefined): 'emerald' | 'amber' | 'red' | 'blue' => {
  if (fcr == null) return 'blue';
  if (fcr < 1.5) return 'emerald';
  if (fcr < 2.0) return 'amber';
  return 'red';
};

export const FeedDetailPage: React.FC = () => {
  const { farmId, recordId } = useParams<{
    farmId: string;
    recordId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pondIdFromUrl = searchParams.get('pondId');
  const { showAxiosError } = useErrorToast();
  const [record, setRecord] = useState<FeedRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!farmId || !recordId) return;

    const loadRecord = async () => {
      try {
        setLoading(true);
        const listResponse = await feedApi.listRecords(farmId, { limit: 100 });
        const recordData = listResponse.data.find(r => r.id === recordId);
        if (!recordData) throw new Error('Record not found');
        setRecord(recordData);
      } catch (error) {
        showAxiosError(error);
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [farmId, recordId, showAxiosError]);

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!record) {
    return <div className="p-8 text-center">Feed record not found</div>;
  }

  const feedUsagePercent = record.maximumFeed > 0
    ? ((record.totalFeed / record.maximumFeed) * 100)
    : 0;

  const feedUsageColor = (): 'emerald' | 'amber' | 'red' => {
    if (feedUsagePercent >= 90) return 'emerald';
    if (feedUsagePercent >= 60) return 'amber';
    return 'red';
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (farmId) {
              navigate(buildRoute.feed(farmId, pondIdFromUrl));
            } else {
              navigate(-1);
            }
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200/50">
            <Utensils className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {record.pondName || `Pond ${record.pondId.slice(0, 8)}`}
            </h1>
            <p className="text-sm text-slate-500">
              {format(new Date(record.feedTime), 'PPpp')}
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(buildRoute.feedEdit(farmId!, recordId!, record.pondId || pondIdFromUrl))}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <StatusBadge variant="info">{record.feedBrand}</StatusBadge>
          <StatusBadge variant="info">{record.feedType}</StatusBadge>
        </div>
      </div>

      {/* Appetite Banner */}
      <Card
        className={`border-l-4 ${
          appetiteColor(record.appetite || 0) === 'emerald'
            ? 'border-l-emerald-500 bg-emerald-50/50'
            : appetiteColor(record.appetite || 0) === 'amber'
            ? 'border-l-amber-500 bg-amber-50/50'
            : 'border-l-red-500 bg-red-50/50'
        }`}
      >
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Appetite Score</p>
              <p className="text-3xl font-bold">
                {appetiteStars(record.appetite || 0)}
                <span className="text-lg font-normal text-slate-400 ml-2">
                  {(record.appetite || 0).toFixed(1)} / 5
                </span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Feeding Performance</p>
              <p
                className={`text-lg font-semibold ${
                  appetiteColor(record.appetite || 0) === 'emerald'
                    ? 'text-emerald-700'
                    : appetiteColor(record.appetite || 0) === 'amber'
                    ? 'text-amber-700'
                    : 'text-red-700'
                }`}
              >
                {(record.appetite || 0) >= 4
                  ? 'Excellent appetite'
                  : (record.appetite || 0) >= 2
                  ? 'Moderate appetite'
                  : 'Low appetite - monitor closely'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricBox
          label="Total Feed"
          value={`${(record.totalFeed || 0).toFixed(1)}kg`}
          icon={Utensils}
          color="blue"
          subtext={`Max: ${(record.maximumFeed || 0).toFixed(1)}kg`}
        />
        <MetricBox
          label="FCR"
          value={record.feedConversionRatio != null ? record.feedConversionRatio.toFixed(2) : '—'}
          icon={TrendingUp}
          color={fcrColor(record.feedConversionRatio)}
        />
        <MetricBox
          label="Feed Usage"
          value={`${feedUsagePercent.toFixed(0)}%`}
          icon={Gauge}
          color={feedUsageColor()}
          subtext="of maximum feed"
        />
        <MetricBox
          label="Maximum Feed"
          value={`${(record.maximumFeed || 0).toFixed(1)}kg`}
          color="slate"
        />
      </div>

      {/* Feed Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-slate-600" />
            Feed Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200/50">
              <p className="text-sm text-slate-600 mb-2">Feed Brand</p>
              <p className="font-semibold text-slate-900">{record.feedBrand}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200/50">
              <p className="text-sm text-slate-600 mb-2">Feed Type</p>
              <p className="font-semibold text-slate-900">{record.feedType}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-600" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {record.notes ? (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-slate-700">{record.notes}</p>
            </div>
          ) : (
            <p className="text-slate-400 italic">No notes recorded</p>
          )}
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card>
        <CardContent className="px-6 py-5">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
            <div>
              <span className="text-slate-500">Performed By</span>
              <div className="text-slate-700 mt-0.5">
                {record.performedByName || <span className="text-slate-400 italic">Unknown</span>}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Fed At</span>
              <div className="text-slate-700 mt-0.5">
                {format(new Date(record.feedTime), 'PPpp')}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Created</span>
              <div className="text-slate-700 mt-0.5">
                {format(new Date(record.createdAt), 'PPpp')}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Record ID</span>
              <div className="font-mono text-slate-700 mt-0.5 text-xs break-all">{record.id}</div>
            </div>
            <div>
              <span className="text-slate-500">Location</span>
              <div className="mt-0.5">
                <LocationDisplay latitude={record.latitude} longitude={record.longitude} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
